import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { useFetcher } from 'react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { useTracker } from '@archipro-website/tracker';
import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import {
    useAuthGateSegment,
    type AuthGateSegment,
} from '~/modules-rocco/user/auth-gate/use-auth-gate-segment';
import { trackAuthGateActionResumed } from '~/modules-rocco/user/auth-gate/auth-gate-events';
import { useFeature } from '~/modules/root';
import { useUserLoggedIn } from '@modules/user';
import { getInboxURL } from '~/modules/chat/utils/url';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import { USER_LOGOUT_API_ROUTE } from '~/routes/remix-api.user.logout';
import { enquirySessionAtom } from '~/modules/enquiry/hooks/use-enquiry';
import { clearEnquiryDraft } from '~/modules/enquiry/util/clear-enquiry-draft';
import { AuthModalBrandPane } from '~/modules-rocco/user/component/auth-modal/AuthModalBrandPane';
import { useGateSubjectValue } from '~/modules-rocco/user/auth-gate/gate-subject';
import { useSocialAuthReturn } from '~/modules-rocco/user/auth-gate/use-social-auth-return';
import {
    resolveGatePaneImage,
    resolveGateThumbnail,
} from '~/modules-rocco/user/auth-gate/gate-pane-image';
import { GateSubjectHeader } from '~/modules-rocco/user/auth-gate/GateSubjectHeader';
import { useEnquirySubmitted } from '~/modules/tracking/hook/useEnquirySubmitted';
import { useToaster } from '~/modules/root/context/ToasterContext';
import {
    ENQUIRE_AUTH_SOURCE_VALUE,
    ENQUIRE_MODAL_SHOWUP_KEY,
} from '~/modules/enquiry/constants';
import {
    useEnquiryEvent,
    useEnquirySession,
} from '~/modules/enquiry/hooks/use-enquiry';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { getEnquiryGateStatement } from '../enquiry-gate-copy';
import { EnquiryGateForm } from './EnquiryGateForm';
import { EnquiryGateSuccess } from './EnquiryGateSuccess';

type GateStep = 'form' | 'resuming' | 'success';

/** Names this gate on every funnel event as well as selecting its intent copy. */
const ENQUIRY_GATE_AUTH_SOURCE = 'enquiry' as const;

/**
 * How long a dismissal stays blocked for an in-flight send before the reader
 * gets their exits back. Long enough for a slow upload, short enough that a
 * hung request is an annoyance rather than a trap.
 */
const SEND_BLOCK_TIMEOUT_MS = 15_000;

const SUCCESS_BRAND_STATEMENT =
    'Get instant access to all ArchiPro features by creating a free account';

/**
 * Revamped two-pane enquiry gate (BC-4833), rendered by EnquiryModal when the
 * auth_modal_2026 flag is on. Same shell as the auth modal: brand pane +
 * scrollable right pane. Steps: enquiry form → (guest) auth gate → success.
 *
 * Driven by the same useEnquiryModal atoms as the legacy modal, so every entry
 * point works unchanged. Guest submits that hit the auth wall switch to the
 * in-pane AuthPanel; on in-modal auth the primed post-login auto-submit
 * (useEnquiryAutoSubmit) resubmits the enquiry without a page reload. Social
 * auth still round-trips and reopens straight into the success step.
 */
export const EnquiryGateModal = () => {
    const { isOpen, context, closeEnquiryModal } = useEnquiryModal();
    const {
        state,
        fetcher,
        enquiryAutoSubmit,
        isAutoSubmitted,
        autoSubmitDelay,
        setEnquiryAutoSubmit,
        setAutoSubmitDelay,
    } = useEnquirySession();
    const newChatUI2025Enabled = useFeature('new_chat_ui_2025');
    const { markSubmitted } = useEnquirySubmitted(context?.siteTreeID);
    const { isLoggedIn } = useUserLoggedIn();
    const { toast } = useToaster();
    const tracker = useTracker();
    const subject = useGateSubjectValue();
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);

    const [step, setStep] = useState<GateStep>('form');
    const [gatePublicUser, setGatePublicUser] = useState<PublicUser>();
    const [authedInModal, setAuthedInModal] = useState(false);
    /** Read by `handleSuccess`, which is memoised without `authedInModal`. */
    const authedInModalRef = useRef(false);
    authedInModalRef.current = authedInModal;
    /** The in-modal auth step completed a signup (vs a login). */
    const [authedIsSignUp, setAuthedIsSignUp] = useState(false);
    /**
     * The far side of a social round-trip this gate started. Single use — see
     * {@link useSocialAuthReturn}; the modal outlives the journey it describes.
     */
    const {
        take: takeSocialAuth,
        spend: spendSocialAuth,
        seen: socialAuth,
    } = useSocialAuthReturn(ENQUIRE_AUTH_SOURCE_VALUE);
    const socialSignUpReturn = socialAuth.signUpReturn;

    const gateRef = useRef<AuthGateSegment | null>(null);
    /** This gate has been open at least once since the last close. */
    const hasOpenedRef = useRef(false);
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;
    /** Routed through a ref: the handler is defined below the subscription. */
    const handleResubmitFailedRef = useRef<() => void>(() => {});
    /** Read by the gate-resolved handler, which is memoised without deps. */
    const autoSubmitPrimedRef = useRef(false);
    autoSubmitPrimedRef.current = enquiryAutoSubmit;

    // Actively resubmitting after auth (as opposed to merely primed): the guest
    // gate leaves `enquiryAutoSubmit` true while the auth step is shown, held
    // back by `autoSubmitDelay`. The resubmit only runs once the delay clears
    // (in-modal auth) or resets on the social reload, so gate the "sending"
    // overlay on that, not on the primed flag alone. A guest can carry a stale
    // primed flag (refresh mid-auth, abandoned social return) — the resubmit
    // needs a session, so never treat a logged-out visitor as resuming.
    const isResuming =
        isLoggedIn &&
        ((enquiryAutoSubmit && !autoSubmitDelay) ||
            (isAutoSubmitted && state !== 'idle'));

    // Secure Sign-In (BC-4831): the ask is a step between auth and success —
    // [form] → [auth] → [ask enable bio] → [success]. The enquiry resubmit
    // keeps running behind the ask, so the send stays instant; only what the
    // user sees is sequenced.
    const stepRef = useRef<GateStep>('form');
    stepRef.current = step;
    const pendingSuccessRef = useRef(false);

    // Any successful enquiry submit (direct, in-modal resubmit or social
    // auto-submit) records the page as enquired, then shows success — unless
    // the user just authenticated and this device qualifies for the Secure
    // Sign-In ask, which is sequenced first.
    const handleSuccess = useCallback(() => {
        if (!isOpenRef.current) return;
        markSubmitted();
        const askOnScreen = gateRef.current?.step === 'secure-sign-in';
        const data = fetcher.data;
        const generatedMember = !!(
            data &&
            'success' in data &&
            data.data?.__typename === 'CreateEnquiryResult' &&
            data.data.GeneratedMember
        );
        // A send that followed an auth step in this gate is a resumed action,
        // and the only place the resume is known to have landed.
        //
        // Reported BEFORE the Secure Sign-In branch below, not after: the
        // resubmit deliberately runs behind that ask so the send stays
        // instant, which makes "landed while the ask is on screen" the normal
        // path for this gate rather than an edge case. Returning first dropped
        // exactly the conversions the flagship flow is built to produce.
        // Spent here, on the one success it describes: this modal is mounted
        // for the whole visit, so a flag left set would make every later
        // enquiry re-enter the auth segment and greet a returning member as new.
        const fromSocial = takeSocialAuth();
        if (
            askOnScreen ||
            authedInModalRef.current ||
            fromSocial.authReturn ||
            generatedMember
        ) {
            trackAuthGateActionResumed({
                tracker,
                authSource: ENQUIRY_GATE_AUTH_SOURCE,
                landed: true,
            });
        }
        if (askOnScreen) {
            // Already asking (in-modal auth path) — success shows on resolve.
            pendingSuccessRef.current = true;
            return;
        }
        // Social round-trips and auto-created accounts land here without an
        // in-modal auth step; their "just logged in" moment is this success.
        // The segment decides whether that earns the ask, and resolves straight
        // through when it does not.
        if (fromSocial.authReturn || generatedMember) {
            pendingSuccessRef.current = true;
            // A generated account is certainly new, and so is a social signup;
            // a social *login* is a returning member, and calling them new
            // re-offers Secure Sign-In on a device that holds their passkey.
            gateRef.current?.enterAuthenticated({
                isNewAccount: generatedMember || fromSocial.signUpReturn,
            });
            return;
        }
        setStep('success');
    }, [markSubmitted, fetcher, takeSocialAuth, tracker]);
    useEnquiryEvent({
        onSuccess: handleSuccess,
        onFailure: () => handleResubmitFailedRef.current(),
    });

    // Fresh open (or after a plain close) starts on the form.
    useEffect(() => {
        if (isOpen) {
            hasOpenedRef.current = true;
            return;
        }
        setStep('form');
        setGatePublicUser(undefined);
        setAuthedInModal(false);
        setAuthedIsSignUp(false);
        pendingSuccessRef.current = false;
        // The journey the social return described has ended, whether or not it
        // reached a success, so the signal must not outlive it.
        //
        // Only on a close that FOLLOWS an opening. "Not open" is also the
        // state at first mount, and on a social return the gate has not opened
        // yet — the auto-submit opens it once the session is restored. Spending
        // it there cleared the signal before the send could read it, which
        // silently dropped Secure Sign-In and the new-account success screen
        // from every social login.
        if (hasOpenedRef.current) {
            hasOpenedRef.current = false;
            spendSocialAuth();
        }
        gateRef.current?.reset();
    }, [isOpen, spendSocialAuth]);

    const handleAuthRequired = useCallback((publicUser: PublicUser) => {
        setGatePublicUser(publicUser);
        gateRef.current?.open({
            publicUser,
            flow:
                publicUser.type === 'new'
                    ? 'post-enquiry-sign-up'
                    : 'post-enquiry-login',
            message:
                publicUser.type === 'new'
                    ? 'Create an account to submit your enquiry'
                    : 'Log in to submit your enquiry',
        });
    }, []);

    /**
     * "Not you? Switch account". Signs the member out and hands the surface
     * straight to the auth step, keeping the modal and the drafted message —
     * the reader is correcting who is sending, not abandoning the enquiry.
     *
     * Deliberately not `useLogout`: that hook reloads the page when the
     * request lands, which would take the modal and the draft with it. The
     * logout response revalidates the root loader, so `isLoggedIn` flips
     * without one.
     */
    const switchAccountFetcher = useFetcher();
    const switchingAccountRef = useRef(false);
    const setEnquirySession = useSetAtom(enquirySessionAtom);
    const handleSwitchAccount = useCallback(() => {
        switchingAccountRef.current = true;
        setEnquirySession(null);
        setEnquiryAutoSubmit(false);
        // Emptied, not closed: this path stays on the page, so latching the
        // draft shut would no-op every save for the rest of the visit —
        // including the one the member signing in next is about to build.
        clearEnquiryDraft({ close: false });
        switchAccountFetcher.submit(
            {},
            {
                method: 'POST',
                action: USER_LOGOUT_API_ROUTE,
            },
        );
    }, [switchAccountFetcher, setEnquirySession, setEnquiryAutoSubmit]);

    useEffect(() => {
        if (!switchingAccountRef.current) return;
        if (switchAccountFetcher.state !== 'idle') return;
        switchingAccountRef.current = false;
        // Only once the session is actually gone. A failed logout leaves the
        // member signed in, and showing them a login step then puts the
        // surface and the session at odds about who is sending.
        if (switchAccountFetcher.data?.success === false) {
            toast('We couldn’t sign you out. Please try again.', {
                type: 'error',
            });
            return;
        }
        gateRef.current?.open({
            flow: 'post-enquiry-login',
            message: 'Log in to submit your enquiry',
        });
    }, [switchAccountFetcher.state, switchAccountFetcher.data, toast]);

    // In-modal (password / email-code) auth finished without a reload. Clear
    // the delay guard so useEnquiryAutoSubmit resubmits the enquiry now that
    // the session is authenticated. When this device qualifies, the Secure
    // Sign-In ask shows immediately (the send keeps running behind it);
    // otherwise the resuming overlay → success as before.
    const handleAuthenticated = useCallback(
        (result?: { isSignUp: boolean }) => {
            setAuthedInModal(true);
            setAuthedIsSignUp(!!result?.isSignUp);
            setAutoSubmitDelay(false);
        },
        [setAutoSubmitDelay],
    );

    // The segment is behind the reader: show success if the enquiry has already
    // landed. Otherwise fall back towards the form, where a still-running
    // resubmit renders the sending overlay over it.
    //
    // Deliberately says nothing about failure. At this moment the only error in
    // `fetcher.data` is the auth wall the guest submit hit on the way in — the
    // very thing that opened this gate — and the resubmit it triggers has not
    // reported yet. Reading it here announced a failure over a send that was
    // still in flight and about to succeed.
    const handleGateResolved = useCallback(() => {
        if (pendingSuccessRef.current) {
            setStep('success');
            return;
        }
        // A primed resubmit means the send is the next thing to happen, so hold
        // the sending state rather than dropping back to the form. `isResuming`
        // cannot answer this yet: it waits on `isLoggedIn`, and the passkey step
        // refreshes the session asynchronously — for the frames in between, the
        // reader watched their filled-in form reappear as though nothing had
        // happened. A failed resubmit puts them back on it.
        setStep(autoSubmitPrimedRef.current ? 'resuming' : 'form');
    }, []);

    // The resubmit itself came back an error. This is the only moment the send
    // is known to have failed, so it is the only place that says so.
    const handleResubmitFailed = useCallback(() => {
        if (!isOpenRef.current) return;
        // A resume that did not land. Counting it as a conversion because the
        // reader authenticated is exactly the error this event prevents.
        trackAuthGateActionResumed({
            tracker,
            authSource: ENQUIRY_GATE_AUTH_SOURCE,
            landed: false,
        });
        toast('We couldn’t send your enquiry. Please try again.', {
            type: 'error',
        });
        setStep('form');
    }, [toast, tracker]);
    handleResubmitFailedRef.current = handleResubmitFailed;

    const gate = useAuthGateSegment({
        authSource: ENQUIRY_GATE_AUTH_SOURCE,
        onAuthenticated: handleAuthenticated,
        onResolved: handleGateResolved,
    });
    gateRef.current = gate;

    const handleClose = useCallback(() => {
        // Records the skip when leaving from the ask.
        gateRef.current?.close();
        if (authedInModal) {
            // Full reload for tracker / GTM reset parity with the redirect-based
            // auth path (the social round-trip already reloaded). Drop the
            // enquire param so a URL-triggered open doesn't resurrect the modal.
            const url = new URL(window.location.href);
            url.searchParams.delete(ENQUIRE_MODAL_SHOWUP_KEY);
            window.location.href = url.toString();
            return;
        }
        if (gateRef.current?.isActive) {
            // Cancel the primed post-login auto-submit so a later navigation
            // does not silently fire the enquiry after the user backed out.
            setEnquiryAutoSubmit(false);
            setAutoSubmitDelay(false);
        }
        closeEnquiryModal();
    }, [
        authedInModal,
        closeEnquiryModal,
        setEnquiryAutoSubmit,
        setAutoSubmitDelay,
    ]);

    // Continue the normal onboarding flow (full load — also resets trackers
    // when the account was created moments ago in this modal).
    const handleExplore = useCallback(() => {
        window.location.href = '/post-sign-up';
    }, []);

    // The account is new when the in-modal auth step reported a signup, the
    // social round-trip returned from one, or the guest submit auto-created a
    // member (GeneratedMember in the response). The auth-wall snapshot is the
    // fallback — the user can switch email mid-auth, so what actually
    // authenticated wins.
    const isNewUser = useMemo(() => {
        if (authedInModal) return authedIsSignUp;
        if (socialSignUpReturn) return true;
        if (gatePublicUser?.type === 'new') return true;
        const data = fetcher.data;
        return !!(
            data &&
            'success' in data &&
            data.data?.__typename === 'CreateEnquiryResult' &&
            data.data.GeneratedMember
        );
    }, [
        authedInModal,
        authedIsSignUp,
        socialSignUpReturn,
        gatePublicUser?.type,
        fetcher.data,
    ]);

    // The backend only returns GeneratedMember when the submit itself created
    // the account. Authing in-modal or via social means one already existed, so
    // this is exactly the silently-created case.
    const accountAutoCreated = useMemo(() => {
        const data = fetcher.data;
        return !!(
            data &&
            'success' in data &&
            data.data?.__typename === 'CreateEnquiryResult' &&
            data.data.GeneratedMember
        );
    }, [fetcher.data]);

    const conversationLink = useMemo(() => {
        if (!newChatUI2025Enabled) {
            return { to: '/member/inbox', isNonRemixRoute: true };
        }
        let conversationID: number | null = null;
        const data = fetcher.data;
        if (
            data &&
            'success' in data &&
            data.data?.__typename === 'CreateEnquiryResult' &&
            data.data.Conversation?.ID
        ) {
            const parsed = Number(data.data.Conversation.ID);
            conversationID =
                Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
        }
        return {
            to: getInboxURL(null, conversationID),
            isNonRemixRoute: false,
        };
    }, [newChatUI2025Enabled, fetcher.data]);

    // A send in flight must not be dismissed by a stray tap or swipe. But a
    // fetcher has no timeout of its own, so on a stalled connection that lock
    // was permanent: X, Escape, outside tap and swipe all dead, with a page
    // reload the only way out. Hold it only as long as a send could plausibly
    // still be running.
    //
    // Computed here rather than from `showSending` below, because the hooks
    // this feeds have to sit above the `context` early return.
    const sendInFlight =
        (step !== 'success' &&
            !gate.isActive &&
            (isResuming || step === 'resuming')) ||
        state === 'submitting';
    const [sendBlockExpired, setSendBlockExpired] = useState(false);
    useEffect(() => {
        if (!sendInFlight) {
            setSendBlockExpired(false);
            return;
        }
        const timer = setTimeout(
            () => setSendBlockExpired(true),
            SEND_BLOCK_TIMEOUT_MS,
        );

        return () => clearTimeout(timer);
    }, [sendInFlight]);
    const dismissBlocked = sendInFlight && !sendBlockExpired;

    if (!context) return null;

    const professionalTitle = context.professional.Title;
    const showSuccess = step === 'success';
    const showGate = !showSuccess && gate.isActive;
    // Only the post-auth resume takes the whole pane, because only then is
    // there no form on screen to report progress. A first submit reports on its
    // own button and must keep the form mounted while it does: the success
    // event is raised from the form's own hook, and the app-level auto-submit
    // only answers for enquiries it sent itself, so unmounting mid-flight
    // leaves nothing to end the wait.
    const showSending =
        !showSuccess && !showGate && (isResuming || step === 'resuming');
    // The signup pitch only makes sense on the new-account success screen;
    // everywhere else the pane reflects the enquiry intent.
    const brandStatement =
        showSuccess && isNewUser
            ? SUCCESS_BRAND_STATEMENT
            : getEnquiryGateStatement(context.itemType, {
                  professionalTitle,
                  relatedItem: context.relatedItem,
              });
    // What the CTA supplied, then what the page is about. A stock photo behind
    // an enquiry for a named item is a defect, so the resolver reports it.
    const brandImage = resolveGatePaneImage({
        override: context.brandImage ?? context.itemImage,
        subject,
        gate: `enquiry:${context.itemType}`,
    });

    return (
        <TwoPaneModal
            corners="rounded"
            // The form pins its own action, so the hint sits above that bar
            // rather than on it. The bar's own scale: its bottom edge now sits
            // 3.5 up (level with the pane), and it stands `pt-4` plus the
            // button's `h-14` tall, so the separator is at 21.5 — and 2 more is
            // the gap.
            scrollHintClassName={isMobile ? 'bottom-24' : 'bottom-23.5'}
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (nextOpen) return;
                // Block dismissal while a submit / resubmit is in flight, and
                // while the gate is holding for one that is about to start.
                // `submitting`, not `!== idle`: the trailing `loading` is the
                // route revalidation after the action, by which point the
                // confirmation is on screen and Close must work.
                if (dismissBlocked) return;
                handleClose();
            }}
            title={
                showSuccess
                    ? 'Enquiry sent'
                    : `Send a message to ${professionalTitle}`
            }
            // A swipe never reaches onPointerDownOutside, so the sheet needs the
            // in-flight guard stated directly or a drag would dismiss a send.
            dismissible={!dismissBlocked}
            onPointerDownOutside={(e) => {
                if (dismissBlocked) {
                    e.preventDefault();
                }
            }}
            pane={
                <AuthModalBrandPane
                    statement={brandStatement}
                    image={brandImage}
                    note={
                        showSuccess
                            ? undefined
                            : context.professional.CustomEnquiryMessage
                    }
                />
            }
        >
            {showSuccess ? (
                <EnquiryGateSuccess
                    variant={isNewUser ? 'new' : 'existing'}
                    professionalTitle={professionalTitle}
                    conversationLink={conversationLink}
                    accountAutoCreated={accountAutoCreated}
                    onClose={handleClose}
                    onExplore={handleExplore}
                />
            ) : showGate ? (
                gate.element
            ) : showSending ? (
                <div className="mx-auto my-auto flex w-full max-w-90.75 flex-col items-center justify-center gap-4 py-16">
                    <Loader2 className="size-9 animate-spin text-[#828282]" />
                    <p className="text-[1.0625rem] text-[#828282]">
                        Sending your enquiry…
                    </p>
                </div>
            ) : (
                <>
                    {/* The sheet drops the brand pane, and with it the item's
                        photograph and the statement naming what this enquiry is
                        about. Without a stand-in the form opens with no subject
                        at all and reads as a generic contact form. Keyed off
                        `isMobile` rather than `md:hidden` for the same reason
                        the shell is: a phone in landscape gets the sheet while
                        measuring wider than `md`, and the CSS rule would have
                        hidden this on exactly the surface with no pane to fall
                        back on. */}
                    {isMobile ? (
                        <GateSubjectHeader
                            pinned
                            className="mb-5"
                            image={resolveGateThumbnail({
                                override: context.itemImage,
                                subject,
                            })}
                            logo={context.professional.CompanyLogo}
                            logoBackground={
                                context.professional.LogoBackgroundColor
                            }
                            title={context.itemTitle}
                            professionalTitle={professionalTitle}
                            logoPending={context.logoPending}
                        />
                    ) : null}
                    <EnquiryGateForm
                        professional={context.professional}
                        itemType={context.itemType}
                        siteTreeID={context.siteTreeID}
                        source={context.source ?? 'EnquiryModal'}
                        relatedItem={context.relatedItem}
                        includeBranch={context.includeBranch}
                        itemTitle={context.messageSubject ?? context.itemTitle}
                        ctaLabel={context.ctaLabel}
                        onAuthRequired={handleAuthRequired}
                        onSwitchAccount={handleSwitchAccount}
                    />
                </>
            )}
        </TwoPaneModal>
    );
};
